import React, { useEffect, useState } from "react";
import { Music2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/context/LangContext";

const PRIVACY_EN = { PUBLIC_TO_EVERYONE: "Everyone", MUTUAL_FOLLOW_FRIENDS: "Friends", FOLLOWER_OF_CREATOR: "Followers", SELF_ONLY: "Only me (private)" };

/** إعدادات النشر على تيك توك لمنشور واحد — تشترطها تيك توك: اسم الحساب، خصوصية يختارها العميل، التفاعل، الإفصاح التجاري، والموافقة. */
export const TikTokPostOptions = ({ open, onOpenChange, campaignId, post, projectId, onSaved }) => {
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const cur = post?.tiktok || {};
  const [privacy, setPrivacy] = useState(cur.privacy_level || "");
  const [allowComment, setAllowComment] = useState(!cur.disable_comment);
  const [allowDuet, setAllowDuet] = useState(!cur.disable_duet);
  const [allowStitch, setAllowStitch] = useState(!cur.disable_stitch);
  const [commercial, setCommercial] = useState(!!(cur.brand_content_toggle || cur.brand_organic_toggle));
  const [organic, setOrganic] = useState(!!cur.brand_organic_toggle);
  const [branded, setBranded] = useState(!!cur.brand_content_toggle);
  const [consent, setConsent] = useState(!!cur.consent);

  useEffect(() => {
    if (!open) return;
    setErr(""); setInfo(null);
    api.get("/social/tiktok/creator-info", { params: { project_id: projectId } }).then((r) => {
      setInfo(r.data);
      if (r.data.audited === false && (r.data.privacy_options || []).includes("SELF_ONLY")) setPrivacy("SELF_ONLY");
    }).catch((e) => setErr(formatError(e.response?.data?.detail)));
  }, [open, projectId]);

  const preAudit = info && info.audited === false;
  const privacyOptions = preAudit ? (info.privacy_options || []).filter((p) => p === "SELF_ONLY") : (info?.privacy_options || []);

  const canSave = privacy && consent && (!commercial || organic || branded) && !(branded && privacy === "SELF_ONLY");

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/social/campaigns/${campaignId}/posts/${post.id}/tiktok`, {
        privacy_level: privacy, disable_comment: !allowComment, disable_duet: !allowDuet, disable_stitch: !allowStitch,
        brand_content_toggle: commercial && branded, brand_organic_toggle: commercial && organic, is_aigc: true, consent,
      });
      toast.success(t("حُفظت إعدادات تيك توك", "TikTok settings saved"));
      onSaved?.(r.data.tiktok);
      onOpenChange(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const Row = ({ label, checked, onChange, disabled, hint, testId }) => (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-secondary/40 px-3 py-2">
      <div><div className="text-sm">{label}</div>{hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}</div>
      <Switch checked={checked && !disabled} onCheckedChange={onChange} disabled={disabled} data-testid={testId} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="tiktok-options-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Music2 className="w-4 h-4" /> {t("إعدادات النشر على تيك توك", "TikTok post settings")}</DialogTitle>
          <DialogDescription>{t("تشترط تيك توك أن تختار هذه الإعدادات بنفسك قبل نشر الفيديو.", "TikTok requires you to choose these settings yourself before the video is posted.")}</DialogDescription>
        </DialogHeader>
        {err && <div className="text-xs text-destructive rounded-lg bg-destructive/10 p-2" data-testid="tiktok-options-error">{err}</div>}
        {!info && !err && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> {t("جارٍ جلب بيانات الحساب…", "Loading account…")}</div>}
        {info && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm" data-testid="tiktok-creator">
              {info.avatar && <img src={info.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />}
              <span>{t("سيُنشر على حساب", "Posting to")} <b>{info.nickname || info.username}</b></span>
            </div>
            <div>
              <div className="text-xs font-bold mb-1.5">{t("من يستطيع مشاهدة المنشور؟", "Who can view this post?")}</div>
              {preAudit && (
                <p className="text-[11px] text-amber-700 dark:text-amber-300 mb-2" data-testid="tiktok-audit-note">
                  {privacyOptions.length
                    ? t("مرحلة ما قبل اعتماد تيك توك للتطبيق: تشترط تيك توك أن يكون الحساب «خاصًا» وأن يُنشر بخيار «أنا فقط» (يراه صاحب الحساب فقط). بعد اعتماد التطبيق تُفتح بقية الخيارات والنشر العام.",
                        "Until TikTok approves the app, TikTok requires a Private account and the \"Only me\" option (visible to the account owner only). Public posting unlocks after approval.")
                    : t("حساب تيك توك هذا عام (Public). قبل اعتماد التطبيق تشترط تيك توك حسابًا «خاصًا» — اجعله خاصًا من إعدادات تيك توك ثم أعد فتح هذه النافذة.",
                        "This TikTok account is Public. Until the app is approved, TikTok requires a Private account — switch it to Private in TikTok settings and reopen this dialog.")}
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                {privacyOptions.map((p) => (
                  <button key={p} type="button" onClick={() => setPrivacy(p)} data-testid={`tiktok-privacy-${p}`}
                    className={`rounded-xl border px-3 py-2 text-xs text-start transition-colors ${privacy === p ? "border-primary bg-primary/10 font-bold" : "border-border bg-secondary/40 hover:bg-secondary"} ${branded && p === "SELF_ONLY" ? "opacity-40 cursor-not-allowed" : ""}`}
                    disabled={branded && p === "SELF_ONLY"}>
                    {t(info.privacy_names?.[p] || p, PRIVACY_EN[p] || p)}
                  </button>
                ))}
              </div>
              {!privacy && <div className="text-[11px] text-amber-600 mt-1">{t("اختر مستوى الخصوصية", "Choose a privacy level")}</div>}
            </div>
            <Row label={t("السماح بالتعليقات", "Allow comments")} checked={allowComment} onChange={setAllowComment} disabled={info.comment_disabled} hint={info.comment_disabled ? t("معطّل من إعدادات حسابك", "Disabled in your TikTok settings") : ""} testId="tiktok-allow-comment" />
            <Row label={t("السماح بالدويت (Duet)", "Allow Duet")} checked={allowDuet} onChange={setAllowDuet} disabled={info.duet_disabled} hint={info.duet_disabled ? t("معطّل من إعدادات حسابك", "Disabled in your TikTok settings") : ""} testId="tiktok-allow-duet" />
            <Row label={t("السماح بالستيتش (Stitch)", "Allow Stitch")} checked={allowStitch} onChange={setAllowStitch} disabled={info.stitch_disabled} hint={info.stitch_disabled ? t("معطّل من إعدادات حسابك", "Disabled in your TikTok settings") : ""} testId="tiktok-allow-stitch" />
            <Row label={t("الإفصاح عن محتوى تجاري", "Disclose commercial content")} checked={commercial} onChange={(v) => { setCommercial(v); if (!v) { setOrganic(false); setBranded(false); } }} hint={t("فعّله إن كان الفيديو يروّج لعلامتك أو لطرف ثالث", "Turn on if the video promotes your brand or a third party")} testId="tiktok-commercial" />
            {commercial && (
              <div className="grid grid-cols-2 gap-2 ps-2">
                <label className="flex items-center gap-2 text-xs"><Checkbox checked={organic} onCheckedChange={(v) => setOrganic(!!v)} data-testid="tiktok-organic" /> {t("علامتي التجارية", "Your brand")}</label>
                <label className="flex items-center gap-2 text-xs"><Checkbox checked={branded} onCheckedChange={(v) => { setBranded(!!v); if (v && privacy === "SELF_ONLY") setPrivacy(""); }} data-testid="tiktok-branded" /> {t("محتوى إعلاني لطرف ثالث", "Branded content")}</label>
                {commercial && !organic && !branded && <div className="col-span-2 text-[11px] text-amber-600">{t("اختر نوع المحتوى التجاري", "Select the commercial content type")}</div>}
              </div>
            )}
            <label className="flex items-start gap-2 text-xs rounded-xl border border-border p-3 bg-secondary/40">
              <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="tiktok-consent" />
              <span>
                {t("بالنشر، أوافق على ", "By posting, you agree to TikTok's ")}
                {branded && <><a href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noreferrer" className="underline text-primary">{t("سياسة المحتوى الإعلاني", "Branded Content Policy")}</a>{t(" و", " and ")}</>}
                <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer" className="underline text-primary">{t("إقرار استخدام الموسيقى في تيك توك", "Music Usage Confirmation")}</a>.
                {" "}{t("قد تستغرق معالجة الفيديو لدى تيك توك بضع دقائق قبل ظهوره.", "TikTok may take a few minutes to process the video before it appears.")}
              </span>
            </label>
            <Button onClick={save} disabled={!canSave || saving} className="w-full gap-2" data-testid="tiktok-options-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {t("حفظ إعدادات النشر", "Save post settings")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
